/**
 * InstanceUpload — what Manage Portfolio shows (EIA) while the portfolio has no
 * instances yet: a short explanation and an Upload File button, so the two ways
 * to fill a portfolio (the Add instance form above, or a file) are both obvious.
 *
 * Built on the library's EmptyState (dashed frame, icon tile, title, text,
 * action) rather than hand-rolled. The page decides WHEN to show it; this only
 * draws it and hands an accepted file to `onUpload`. Without `onUpload` (the
 * user may not upload) there is no button, and the text says only what is left.
 *
 * `selfPerf` (EIA): the card also takes the Self Perf Assessment file, dropped
 * together with the instances file (first, then second). `onUpload(file,
 * selfPerfFile)` gets the second as its own argument; with one file it is
 * `onUpload(file)`, as before.
 *
 * Project rule note (.claude/rules/variants.md): prefer one shared component
 * with EIA-only parts behind <VariantGate only="eia"> over per-product folders.
 * If this grows a CCA twin, merge the two instead.
 */
import { useState } from 'react';

import { CloudUploadIcon, ServerIcon } from '@components/common/icons';
import { EmptyState } from '@components/ui';
import { cx } from '@lib/classNames';

import { UploadFileButton } from '../UploadFileButton';
import { useFileDrop } from '../useFileDrop';

import styles from './InstanceUpload.module.css';

/* The card's wording is the user guide's (user order 2026-10-26). */
const BULK_UPLOAD_TEMPLATE =
  ' — use the template (available at the top of the page) for the correct format.';
const BULK_UPLOAD_TEXT = `import in bulk by uploading a file${BULK_UPLOAD_TEMPLATE}`;
const DROP_HINT = 'You can also drag and drop a file here.';

export function InstanceUpload({ onUpload, canAdd = true, disabled = false, selfPerf = false }) {
  const [error, setError] = useState('');
  const canUpload = typeof onUpload === 'function';

  const description =
    canAdd && canUpload
      ? `Add an instance using the fields above, or ${BULK_UPLOAD_TEXT}`
      : canAdd
        ? 'Add an instance using the fields above.'
        : canUpload
          ? `Import in bulk by uploading a file${BULK_UPLOAD_TEMPLATE}`
          : 'This portfolio has no instances.';

  const takeFile = (file, selfPerfFile = null) => {
    setError('');
    // The second argument only when there is a second file.
    if (selfPerfFile) onUpload(file, selfPerfFile);
    else onUpload(file);
  };
  // The whole card takes a dropped file, same checks as the button. With the
  // Self Perf slot (EIA) it takes two: the first is the instances file, the
  // second the Self Perf Assessment file.
  const { dropProps, dragging } = useFileDrop({
    onFile: takeFile,
    onFiles: ([file, selfPerfFile]) => takeFile(file, selfPerfFile),
    onReject: setError,
    disabled: disabled || !canUpload,
    max: selfPerf ? 2 : 1,
  });

  return (
    <div className={cx(styles.drop, dragging && styles.dragging)} {...dropProps}>
      <EmptyState
        className={styles.empty}
        icon={<ServerIcon />}
        title="No instances added yet"
        description={canUpload && !disabled ? `${description} ${DROP_HINT}` : description}
        action={
          !canUpload ? undefined : (
            <div className={styles.action}>
              <UploadFileButton
                variant="secondary"
                icon={<CloudUploadIcon />}
                className={styles.uploadButton}
                disabled={disabled}
                onFile={takeFile}
                onReject={setError}
              />
              {error ? (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          )
        }
      />
    </div>
  );
}

export default InstanceUpload;
